import { useState, useEffect } from 'react';
import { supabase, MenuItem } from '../lib/supabase';
import { Search, UtensilsCrossed, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type MenuItemWithRestaurant = MenuItem & {
  restaurants?: { id: string; name: string; logo_url: string | null };
};

export default function SearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MenuItemWithRestaurant[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim().length >= 2) {
        searchFood();
      } else {
        setResults([]);
        setHasSearched(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const searchFood = async () => {
    setLoading(true);
    setHasSearched(true);

    const { data, error } = await supabase
      .from('menu_items')
      .select('*, restaurants(id, name, logo_url)')
      .eq('is_available', true)
      .ilike('name', `%${query.trim()}%`)
      .limit(50);

    if (!error) {
      setResults((data as MenuItemWithRestaurant[]) || []);
    }
    setLoading(false);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
  };

  const handleItemClick = (item: MenuItemWithRestaurant) => {
    if (item.restaurants?.id) {
      navigate(`/restaurant/${item.restaurants.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="sticky top-14 z-30 bg-gray-50 dark:bg-gray-900 px-4 py-4 border-b border-gray-200 dark:border-gray-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk etter mat..."
            className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#004D99] dark:focus:ring-orange-400"
            autoFocus
          />
          {query && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-6">
        {loading && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Søker...
          </div>
        )}

        {!loading && hasSearched && results.length === 0 && (
          <div className="text-center py-12">
            <UtensilsCrossed className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              Fant ingen retter som matcher "{query}"
            </p>
          </div>
        )}

        {!loading && !hasSearched && (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              Søk etter favorittmaten din
            </p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-3">
            {results.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className="w-full bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all active:scale-[0.98] text-left"
              >
                <div className="flex items-start gap-3">
                  {item.restaurants?.logo_url ? (
                    <img
                      src={item.restaurants.logo_url}
                      alt={item.restaurants.name}
                      className="w-12 h-12 object-contain rounded-lg bg-gray-100 dark:bg-gray-700 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <UtensilsCrossed className="w-6 h-6 text-[#004D99] dark:text-orange-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                      {item.name}
                    </h3>
                    {item.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                        {item.description}
                      </p>
                    )}
                    {item.restaurants?.name && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {item.restaurants.name}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-lg font-bold text-[#004D99] dark:text-orange-400">
                      {item.price} kr
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
